import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { StreakService } from '../../services/StreakService';
import { XpService } from '../../services/XpService';
import { SoundEffectService } from '../../services/SoundEffectService';
import { LearningProgressService } from '../../services/LearningProgressService';
import { ExerciseScoreService } from '../../services/ExerciseScoreService';
import { ChunkyButton, Confetti, ComboPill, StarRow, FloatingXpBadge } from '../../components/GameUI';

// Falling-balloon vocabulary game — port of the mobile app's
// BalloonPopScreen. The target-language word is spoken, then colored
// balloons carrying native-language translations fall from the top of the
// play area; tap the correct one before it reaches the bottom. Game-state
// numbers (lives/score/combo/round) live in refs so timers and animation
// callbacks always read the latest value instead of a stale render closure,
// the same trick FruitSliceGame/WordSearchGame use — React state is only
// kept in sync for what actually needs to re-render.

const TOTAL_ROUNDS = 8;
const START_LIVES = 3;
const BALLOON_COLORS = ['#FF4B4B', '#1CB0F6', '#58CC02', '#FFC800', '#CE82FF', '#FF9600'];

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function speak(text, lang) {
  try {
    if (!text || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    if (lang) utterance.lang = lang;
    window.speechSynthesis.speak(utterance);
  } catch {
    // speech synthesis is a nice-to-have, never block the game on it
  }
}

export default function BalloonPopGame() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useLocation();
  const {
    pairs = [],
    nativeLang = { code: '', label: '', dir: 'ltr' },
    targetLang = { code: '', label: '', dir: 'ltr' },
    langPair = 'guest',
  } = state || {};

  const containerRef = useRef(null);

  // Game-loop-critical numbers live in refs (mirrors the Dart screen's
  // mutable instance fields) so tap handlers and delayed timers never race
  // against React's async state updates.
  const roundRef = useRef(0);
  const livesRef = useRef(START_LIVES);
  const scoreRef = useRef(0);
  const comboRef = useRef(0);
  const maxComboRef = useRef(0);
  const doneRef = useRef(false);
  const rewardedRef = useRef(false);
  const roundLockedRef = useRef(false);
  const landedHandledRef = useRef(false);
  const currentAnswerRef = useRef(null);
  const elapsedIntervalRef = useRef(null);
  const roundTimeoutRef = useRef(null);
  const particleIdRef = useRef(0);

  const [lives, setLives] = useState(START_LIVES);
  const [score, setScore] = useState(0);
  const [round, setRound] = useState(0);
  const [combo, setCombo] = useState(0);
  const [maxCombo, setMaxCombo] = useState(0);
  const [comboPopKey, setComboPopKey] = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [done, setDone] = useState(false);
  const [balloons, setBalloons] = useState([]);
  const [particles, setParticles] = useState([]);
  const [phase, setPhase] = useState('idle'); // 'idle' | 'start' | 'falling'
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!pairs.length) navigate('/games', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Measure the play area so balloon fall paths can be computed in pixels.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setContainerSize({ width: r.width, height: r.height });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Two rAFs after the balloons mount at their off-screen "start" position,
  // flip to "falling" so the CSS `top` transition actually animates instead
  // of jumping straight to its end value.
  useEffect(() => {
    if (phase !== 'start') return undefined;
    let raf2;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setPhase('falling'));
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [phase]);

  function syncStats() {
    setLives(livesRef.current);
    setScore(scoreRef.current);
    setRound(roundRef.current);
    setCombo(comboRef.current);
    setMaxCombo(maxComboRef.current);
  }

  function startElapsedTimer() {
    if (elapsedIntervalRef.current) clearInterval(elapsedIntervalRef.current);
    elapsedIntervalRef.current = setInterval(() => {
      if (!doneRef.current) setElapsedSeconds((s) => s + 1);
    }, 1000);
  }

  function spawnParticle(x, y, success) {
    const id = particleIdRef.current++;
    const color = success ? '#58CC02' : '#FF4B4B';
    setParticles((cur) => [...cur, { id, x, y, color }]);
    setTimeout(() => {
      setParticles((cur) => cur.filter((p) => p.id !== id));
    }, 450);
  }

  function startRound(idx) {
    landedHandledRef.current = false;
    roundLockedRef.current = false;
    const fallSeconds = Math.min(6, Math.max(3.5, 6 - idx * 0.3));

    const answer = pairs[Math.floor(Math.random() * pairs.length)];
    const distractorPool = shuffle(pairs.filter((p) => p.id !== answer.id));
    const options = shuffle([answer, ...distractorPool.slice(0, 3)]);
    const rawSlots = shuffle(options.map((_, i) => (i + 0.5) / options.length));

    const newBalloons = options.map((option, i) => ({
      uid: `${idx}-${i}-${option.id}-${Math.random().toString(36).slice(2, 8)}`,
      text: option.native,
      isCorrect: option.id === answer.id,
      color: BALLOON_COLORS[Math.floor(Math.random() * BALLOON_COLORS.length)],
      dx: Math.min(0.92, Math.max(0.08, rawSlots[i] ?? 0.5)),
      fallMs: fallSeconds * 1000,
      popping: false,
    }));

    currentAnswerRef.current = answer;
    setBalloons(newBalloons);
    setPhase('start');
    speak(answer.target, targetLang.code);
  }

  function resolveRound(hit) {
    if (roundLockedRef.current) return;
    roundLockedRef.current = true;
    const answer = currentAnswerRef.current;

    if (hit) {
      SoundEffectService.playCorrect();
      if (navigator.vibrate) navigator.vibrate(15);
      comboRef.current += 1;
      if (comboRef.current > maxComboRef.current) maxComboRef.current = comboRef.current;
      if (comboRef.current >= 2) setComboPopKey((k) => k + 1);
      scoreRef.current += 1;
      if (answer) {
        LearningProgressService.record(langPair, 'vocabulary', 1, { activityId: `balloonpop:${answer.id}` });
      }
    } else {
      SoundEffectService.playWrong();
      if (navigator.vibrate) navigator.vibrate(35);
      comboRef.current = 0;
      livesRef.current = Math.max(0, livesRef.current - 1);
    }
    syncStats();

    if (livesRef.current <= 0) {
      finishGame();
      return;
    }
    roundTimeoutRef.current = setTimeout(() => {
      if (doneRef.current) return;
      if (roundRef.current + 1 >= TOTAL_ROUNDS) {
        finishGame();
      } else {
        roundRef.current += 1;
        startRound(roundRef.current);
      }
    }, 500);
  }

  // All balloons in a round share one fall duration, so the first one to
  // finish its `top` transition tells us the round's clock has run out —
  // equivalent to Dart's per-balloon AnimationController status listener,
  // collapsed to a single check since they all complete together.
  function handleBalloonLanded(e) {
    if (e.propertyName !== 'top') return;
    if (landedHandledRef.current) return;
    landedHandledRef.current = true;
    if (roundLockedRef.current || doneRef.current) return;
    resolveRound(false);
  }

  function tapBalloon(balloon, e) {
    if (roundLockedRef.current || doneRef.current) return;
    const rect = containerRef.current?.getBoundingClientRect();
    const x = rect ? e.clientX - rect.left : 0;
    const y = rect ? e.clientY - rect.top : 0;
    spawnParticle(x, y, balloon.isCorrect);

    setBalloons((bs) => bs.map((b) => (b.uid === balloon.uid ? { ...b, popping: true } : b)));
    setTimeout(() => {
      setBalloons((bs) => bs.filter((b) => b.uid !== balloon.uid));
    }, 160);

    if (balloon.isCorrect) {
      resolveRound(true);
    } else {
      SoundEffectService.playWrong();
      if (navigator.vibrate) navigator.vibrate(35);
      comboRef.current = 0;
      livesRef.current = Math.max(0, livesRef.current - 1);
      syncStats();
      if (livesRef.current <= 0) finishGame();
    }
  }

  function xpEarned() {
    const percent = Math.round((scoreRef.current / TOTAL_ROUNDS) * 100);
    const stars = ExerciseScoreService.starsForPercent(percent);
    return scoreRef.current * 4 + (stars === 3 ? 10 : 0);
  }

  function starsFor() {
    const percent = Math.round((scoreRef.current / TOTAL_ROUNDS) * 100);
    return ExerciseScoreService.starsForPercent(percent);
  }

  function finishGame() {
    if (doneRef.current) return;
    doneRef.current = true;
    roundLockedRef.current = true;
    if (elapsedIntervalRef.current) clearInterval(elapsedIntervalRef.current);
    if (roundTimeoutRef.current) clearTimeout(roundTimeoutRef.current);
    setBalloons([]);
    setDone(true);
    awardRewards();
  }

  function awardRewards() {
    if (rewardedRef.current) return;
    rewardedRef.current = true;
    StreakService.recordActivity();
    XpService.addXp(xpEarned());
    if (starsFor() === 3) SoundEffectService.playCelebration();
  }

  function restart() {
    doneRef.current = false;
    rewardedRef.current = false;
    roundLockedRef.current = false;
    landedHandledRef.current = false;
    roundRef.current = 0;
    livesRef.current = START_LIVES;
    scoreRef.current = 0;
    comboRef.current = 0;
    maxComboRef.current = 0;
    setParticles([]);
    setElapsedSeconds(0);
    setDone(false);
    syncStats();
    startElapsedTimer();
    startRound(0);
  }

  // Kick off the game once (and only once — pairs come from route state and
  // never change during this screen's lifetime).
  useEffect(() => {
    if (!pairs.length) return undefined;
    startElapsedTimer();
    startRound(0);
    return () => {
      if (elapsedIntervalRef.current) clearInterval(elapsedIntervalRef.current);
      if (roundTimeoutRef.current) clearTimeout(roundTimeoutRef.current);
      try { window.speechSynthesis && window.speechSynthesis.cancel(); } catch { /* ignore */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!pairs.length) return null;

  const stars = starsFor();
  const perfect = stars === 3;
  const lostEarly = lives <= 0 && round + 1 < TOTAL_ROUNDS;
  const minutes = Math.floor(elapsedSeconds / 60);
  const seconds = elapsedSeconds % 60;
  const elapsedLabel = `${minutes}:${String(seconds).padStart(2, '0')}`;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto flex h-screen max-w-lg flex-col px-4 py-4 sm:px-6">
        <div className="mb-2 flex shrink-0 items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-500 shadow-sm hover:bg-slate-50"
          >
            ← {t('games.title')}
          </button>
          <h1 className="text-sm font-black text-slate-900">{t('games.balloonPop')}</h1>
          <span className="w-16" />
        </div>

        {done ? (
          <div className="relative flex flex-1 flex-col items-center justify-center py-6 text-center">
            {perfect && <Confetti />}
            <span className="text-6xl">{perfect ? '🏆' : (lostEarly ? '💥' : '🎉')}</span>
            <h2 className="mt-3 text-2xl font-black text-slate-900">
              {t('games.balloonPopComplete', 'Balloons popped!')}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              {t('games.score')}: {score}/{TOTAL_ROUNDS}
            </p>
            <p className="mt-1 text-xs text-slate-400">
              {t('games.wordMatchBestCombo', { combo: maxCombo })}
            </p>
            <div className="mt-3">
              <StarRow count={stars} />
            </div>
            <div className="mt-3">
              <FloatingXpBadge xp={xpEarned()} />
            </div>
            <div className="mt-8 w-full max-w-xs">
              <ChunkyButton color="#4F46E5" onClick={restart} className="w-full py-4 text-center text-white">
                {t('games.tryAgain')}
              </ChunkyButton>
              <button
                onClick={() => navigate('/games')}
                className="mt-3 w-full rounded-2xl border border-slate-200 py-3 text-sm font-bold text-slate-600 hover:bg-slate-50"
              >
                {t('games.done')}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-1 flex shrink-0 items-center justify-between">
              <div className="flex items-center gap-0.5 text-lg">
                {Array.from({ length: START_LIVES }).map((_, i) => (
                  <span key={i}>{i < lives ? '❤️' : '🤍'}</span>
                ))}
              </div>
              {combo >= 2 ? <ComboPill key={comboPopKey} combo={combo} /> : <span />}
              <div className="flex items-center gap-1 text-xs font-black text-slate-500">
                <span>⏱</span>
                <span>{elapsedLabel}</span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => speak(currentAnswerRef.current?.target, targetLang.code)}
              className="mb-2 flex shrink-0 items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-2.5 text-sm font-black text-white shadow-md shadow-indigo-300/30"
            >
              <span aria-hidden="true">🔊</span>
              {t('games.balloonPopListen', 'Tap to hear the word')}
            </button>

            <div
              ref={containerRef}
              className="relative min-h-0 flex-1 overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-b from-sky-50 to-white"
            >
              {balloons.map((b) => {
                const leftPx = containerSize.width
                  ? Math.min(Math.max(containerSize.width - 90, 0), Math.max(0, b.dx * containerSize.width - 45))
                  : 0;
                const topPx = phase === 'falling' ? containerSize.height + 110 : -110;
                return (
                  <div
                    key={b.uid}
                    onClick={(e) => tapBalloon(b, e)}
                    onTransitionEnd={handleBalloonLanded}
                    className={`absolute cursor-pointer select-none ${b.popping ? 'balloon-pop-out' : ''}`}
                    style={{
                      left: leftPx,
                      top: topPx,
                      width: 90,
                      transition: `top ${b.fallMs}ms linear`,
                      touchAction: 'manipulation',
                    }}
                  >
                    <div
                      className="flex h-[78px] w-[90px] items-center justify-center rounded-full p-1.5 text-center"
                      style={{ background: b.color, boxShadow: `0 4px 10px ${b.color}80` }}
                    >
                      <span dir={nativeLang.dir} className="line-clamp-2 px-1 text-xs font-black leading-tight text-white">
                        {b.text}
                      </span>
                    </div>
                    <div className="mx-auto h-[18px] w-[2px]" style={{ background: b.color }} />
                  </div>
                );
              })}

              {particles.map((p) => (
                <span
                  key={p.id}
                  className="pointer-events-none absolute h-4 w-4 rounded-full game-particle-pop"
                  style={{ left: p.x - 8, top: p.y - 8, background: p.color }}
                />
              ))}

              <style>{`
                @keyframes game-particle-pop-kf {
                  0% { transform: scale(0.4); opacity: 0.9; }
                  100% { transform: scale(3); opacity: 0; }
                }
                .game-particle-pop { animation: game-particle-pop-kf 450ms ease-out forwards; }
                @keyframes balloon-pop-out-kf {
                  0% { transform: scale(1); opacity: 1; }
                  100% { transform: scale(1.4); opacity: 0; }
                }
                .balloon-pop-out { animation: balloon-pop-out-kf 160ms ease-out forwards; }
              `}</style>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
